import { useApi } from '@/config/apiFetcher'
import { useUserStore } from '@/store/userStore'
import { useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

export interface Organisation {
  organisationID: string
  organisationName: string
}

/**
 * Lists every organisation known to the users table. Backed by
 * `GET /user-management/organisations` (admin-tooling endpoint).
 *
 * The BE endpoint is JWT-gated only — call sites must gate the UI on
 * `isAdminUser()` so customer accounts never see the cross-org list.
 */
export const ORGANISATIONS_QUERY_KEY = ['organisations'] as const

export default () => {
  const api = useApi()
  const baseUrl = import.meta.env.VITE_APP_USER_MANAGEMENT
  const { isLoggedIn } = storeToRefs(useUserStore())

  const useOrganisationsQuery = () =>
    useQuery({
      queryKey: ORGANISATIONS_QUERY_KEY,
      queryFn: async () => {
        const res = await api.get(`${baseUrl}/organisations`)
        if (res.status !== 200) throw new Error('Failed to fetch organisations')
        return (res.data?.organisations ?? []) as Organisation[]
      },
      enabled: computed(() => !!isLoggedIn.value && !!baseUrl),
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      retry: 2,
      retryDelay: 1000,
      refetchOnWindowFocus: false,
    })

  return { useOrganisationsQuery }
}
